import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, NavLink } from 'react-router-dom';
import { Button, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Badge, Spinner } from 'reactstrap';
import { slide as Menu } from 'react-burger-menu';
import {
    FiHome,
    FiCreditCard,
    FiTrendingUp,
    FiShield,
    FiSettings,
    FiUser,
    FiBell,
    FiLogOut,
    FiChevronDown,
    FiDollarSign,
    FiBarChart2,
    FiHelpCircle,
    FiSearch,
    FiPlusCircle,
    FiMenu,
    FiX,
    FiTarget,
    FiPieChart,
    FiUsers,
    FiLayers,
    FiFileText,
    FiGlobe,
    FiVideo,
    FiImage
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext.tsx';

const CustomerLayout = () => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navigate = useNavigate();

    // Use AuthContext
    const { user, userProfile, logout, loading: authLoading } = useAuth();

    // Mock notifications - should come from API
    const notifications = [];

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const handleStateChange = (state) => {
        setIsMenuOpen(state.isOpen);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
    };

    // Get user metadata from auth context
    const getUserMetadata = () => {
        if (!user) return null;

        const authMetadata = user.metadata || {};
        const profileData = userProfile || {};

        return {
            fullName: authMetadata.full_name || profileData.full_name || user.email?.split('@')[0] || 'User',
            email: user.email || 'No email',
            role: user.role || profileData.role || 'customer',
            avatarUrl: authMetadata.avatar_url || profileData.avatar_url,
            plan: authMetadata.plan || profileData.plan || 'Starter'
        };
    };

    const menuItems = [
        { path: '/panel/dashboard', icon: <FiHome />, label: 'Dashboard' },
        { path: '/panel/campaigns', icon: <FiTarget />, label: 'Campaigns' },
        { path: '/panel/NewCampaignWizardPage', icon: <FiTarget />, label: 'New Campaign' },
        { path: '/panel/ads', icon: <FiTrendingUp />, label: 'Ads' },
        { path: '/panel/NewAdWizardPage', icon: <FiUsers />, label: 'New Ad' },
        { path: '/panel/audience', icon: <FiUsers />, label: 'Audience' },
        { path: '/panel/analytics', icon: <FiPieChart />, label: 'Analytics' },
        { path: '/panel/billing', icon: <FiCreditCard />, label: 'Billing' },
        { path: '/panel/creatives', icon: <FiImage />, label: 'Creatives' },
        { path: '/panel/channels', icon: <FiGlobe />, label: 'Channels' },
        { path: '/panel/reports', icon: <FiFileText />, label: 'Reports' },
        { path: '/panel/settings', icon: <FiSettings />, label: 'Settings' },
    ];

    if (authLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100">
                <div className="text-center">
                    <Spinner color="primary" size="lg" />
                    <p className="mt-3">Loading...</p>
                </div>
            </div>
        );
    }

    const userMeta = getUserMetadata();

    // Custom burger menu styles
    const burgerMenuStyles = {
        bmBurgerButton: {
            position: 'fixed',
            width: '24px',
            height: '20px',
            left: '16px',
            top: '16px',
            zIndex: '1001'
        },
        bmBurgerBars: {
            background: '#6c757d',
            height: '2px'
        },
        bmBurgerBarsHover: {
            background: '#495057'
        },
        bmCrossButton: {
            height: '32px',
            width: '32px',
            top: '16px',
            right: '16px'
        },
        bmCross: {
            background: '#adb5bd',
            height: '20px',
            width: '2px'
        },
        bmMenuWrap: {
            position: 'fixed',
            height: '100%',
            top: '0',
            zIndex: '1100'
        },
        bmMenu: {
            background: '#ffffff',
            padding: '0',
            fontSize: '0.875rem',
            overflow: 'hidden',
            borderRight: '1px solid #dee2e6',
            boxShadow: '2px 0 8px rgba(0,0,0,0.05)'
        },
        bmMorphShape: {
            fill: '#f8f9fa'
        },
        bmItemList: {
            color: '#495057',
            padding: '0',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        },
        bmItem: {
            display: 'inline-block',
            width: '100%'
        },
        bmOverlay: {
            background: 'rgba(0, 0, 0, 0.3)',
            top: '0',
            left: '0',
            zIndex: '1099'
        }
    };

    // Menu content component
    const SidebarContent = () => (
        <div className="sidebar-content">
            <div className="sidebar-header py-3 px-3 border-bottom">
                <div className="brand d-flex align-items-center">
                    <div className="brand-icon bg-primary rounded p-1 me-2">
                        <FiTarget size={16} />
                    </div>
                    <h4 className="brand-text mb-0 fs-6 fw-semibold">AdvertPro</h4>
                </div>
            </div>

            {userMeta ? (
                <>
                   

                    <nav className="sidebar-nav p-2">
                        {menuItems.map((item) => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                className={({ isActive }) =>
                                    `sidebar-nav-item d-flex align-items-center py-2 px-3 mb-1 rounded text-decoration-none ${isActive ? 'active' : ''}`
                                }
                                onClick={closeMenu}
                            >
                                <span className="nav-icon me-3">{item.icon}</span>
                                <span className="nav-label fs-6">{item.label}</span>
                            </NavLink>
                        ))}
                    </nav>

                    <div className="sidebar-footer p-3 border-top mt-auto">
                        <div className="account-balance mb-3">
                            <small className="text-muted fs-7">Ad Credits</small>
                            <div className="balance-amount fs-5 fw-semibold text-success">$2,850.25</div>
                            <Button
                                color="outline-secondary"
                                size="sm"
                                className="w-100 d-flex align-items-center justify-content-center"
                                onClick={() => {
                                    closeMenu();
                                    handleLogout();
                                }}
                            >
                                <FiLogOut className="me-2" size={14} />
                                <span className="fs-6">Logout</span>
                            </Button>
                        </div>
                        
                    </div>
                </>
            ) : (
                <div className="sidebar-user p-4 text-center">
                    <div className="text-muted">
                        <FiUser size={32} className="mb-3 text-muted" />
                        <p className="fs-6 mb-3">Not logged in</p>
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => {
                                closeMenu();
                                navigate('/login');
                            }}
                            className="w-100"
                        >
                            Login
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="customer-layout">
            {/* React Burger Menu for mobile */}
            <div className="d-md-none py-5">
                <Menu
                    styles={burgerMenuStyles}
                    isOpen={isMenuOpen}
                    onStateChange={handleStateChange}
                    width={240}
                    pageWrapId="page-wrap"
                    outerContainerId="outer-container"
                    right={false}
                    disableAutoFocus
                    customBurgerIcon={<FiMenu size={20} />}
                    customCrossIcon={<FiX size={20} />}
                >
                    <SidebarContent />
                </Menu>
            </div>

            {/* Fixed Desktop Sidebar */}
            <div className="d-none d-md-block">
                <aside className="desktop-sidebar pb-5">
                    <SidebarContent />
                </aside>
            </div>

            {/* Main Content */}
            <main id="page-wrap" className="main-content">
                {/* Fixed Top Header */}
                <header className={`main-header ${scrolled ? 'scrolled' : ''}`}>
                    <div className="header-left">
                        <button
                            className="sidebar-toggle d-md-none btn btn-sm btn-outline-secondary border-0"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            aria-label="Toggle sidebar"
                        >
                            <FiMenu size={18} />
                        </button>

                        <div className="header-search ms-3">
                            <div className="input-group input-group-sm">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search campaigns, ads..."
                                    style={{ width: '300px' }}
                                />
                                <button className="btn btn-outline-secondary btn-sm" type="button">
                                    <FiSearch size={14} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="header-right">
                        <div className="header-actions">
                            <Button
                                color="primary"
                                size="sm"
                                className="d-flex align-items-center"
                                onClick={() => navigate('/panel/NewCampaignWizardPage')}
                            >
                                <FiPlusCircle className="me-1" size={14} />
                                New Campaign
                            </Button>
                            <Button
                                color="outline-primary"
                                size="sm"
                                className="d-flex align-items-center ms-2"
                                onClick={() => navigate('/panel/NewAdWizardPage')}
                            >
                                <FiImage className="me-1" size={14} />
                                Create Ad
                            </Button>
                        </div>

                        {userMeta && (
                            <>
                                <div className="header-notifications">
                                    <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                                        <DropdownToggle tag="div" className="notification-bell btn btn-sm btn-outline-secondary border-0 position-relative">
                                            <FiBell size={18} />
                                            {notifications.length > 0 && (
                                                <Badge color="danger" pill className="notification-badge">
                                                    {notifications.length}
                                                </Badge>
                                            )}
                                        </DropdownToggle>
                                        <DropdownMenu end className="notification-dropdown fs-6">
                                            <DropdownItem header className="fs-6 fw-semibold">Notifications</DropdownItem>
                                            {notifications.length > 0 ? (
                                                notifications.map((notification) => (
                                                    <DropdownItem key={notification.id} className="notification-item fs-6">
                                                        <div className="notification-content">
                                                            <strong>{notification.title}</strong>
                                                            <small className="text-muted d-block">{notification.message}</small>
                                                            <small className="text-muted">
                                                                {new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </small>
                                                        </div>
                                                    </DropdownItem>
                                                ))
                                            ) : (
                                                <DropdownItem className="text-center text-muted py-3 fs-6">
                                                    No new notifications
                                                </DropdownItem>
                                            )}
                                            <DropdownItem divider />
                                            <DropdownItem onClick={() => navigate('/panel/notifications')} className="fs-6">
                                                View all notifications
                                            </DropdownItem>
                                        </DropdownMenu>
                                    </Dropdown>
                                </div>

                                <div className="header-user">
                                    <Dropdown isOpen={false} toggle={() => { }}>
                                        <DropdownToggle tag="div" className="user-dropdown-toggle d-flex align-items-center btn btn-sm btn-outline-secondary border-0">
                                            <div className="user-avatar-sm me-2">
                                                {userMeta?.avatarUrl ? (
                                                    <img
                                                        src={userMeta.avatarUrl}
                                                        alt={userMeta.fullName?.charAt(0).toUpperCase()}
                                                        className="rounded-circle"
                                                    />
                                                ) : (
                                                    <div className="avatar-placeholder-sm rounded-circle bg-primary d-flex align-items-center justify-content-center">
                                                        <span className="text-white fs-7">
                                                            {userMeta?.fullName?.charAt(0).toUpperCase() || 'U'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="user-info-sm d-none d-lg-block">
                                                <div className="user-name-sm fw-medium fs-6">
                                                    {userMeta?.fullName}
                                                </div>
                                                <div className="user-tier">
                                                    <Badge color="primary" pill className="fs-7">
                                                        {userMeta?.plan || 'Starter'}
                                                    </Badge>
                                                </div>
                                            </div>
                                            <FiChevronDown className="ms-1" size={16} />
                                        </DropdownToggle>
                                        <DropdownMenu end className="user-dropdown-menu fs-6">
                                            <DropdownItem header>
                                                <div className="dropdown-user-info">
                                                    <div className="user-name fw-semibold">{userMeta?.fullName}</div>
                                                    <div className="user-email text-muted fs-7">{userMeta?.email}</div>
                                                    <div className="user-role mt-1">
                                                        <Badge color="info" pill className="fs-7">
                                                            {userMeta?.plan?.toUpperCase()}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </DropdownItem>
                                            <DropdownItem onClick={() => navigate('/panel/profile')} className="fs-6">
                                                <FiUser className="me-2" size={14} />
                                                Profile
                                            </DropdownItem>
                                            <DropdownItem onClick={() => navigate('/panel/billing')} className="fs-6">
                                                <FiCreditCard className="me-2" size={14} />
                                                Billing
                                            </DropdownItem>
                                            <DropdownItem onClick={() => navigate('/panel/settings')} className="fs-6">
                                                <FiSettings className="me-2" size={14} />
                                                Settings
                                            </DropdownItem>
                                            <DropdownItem divider />
                                            <DropdownItem onClick={handleLogout} className="fs-6">
                                                <FiLogOut className="me-2" size={14} />
                                                Logout
                                            </DropdownItem>
                                        </DropdownMenu>
                                    </Dropdown>
                                </div>
                            </>
                        )}

                        {!userMeta && (
                            <div className="header-auth">
                                <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => navigate('/login')}
                                    className="me-2"
                                >
                                    Login
                                </Button>
                                <Button
                                    color="outline-primary"
                                    size="sm"
                                    onClick={() => navigate('/register')}
                                >
                                    Register
                                </Button>
                            </div>
                        )}
                    </div>
                </header>

                {/* Page Content */}
                <div className="content-container">
                    <div className="content-wrapper">
                        <Outlet />
                    </div>

                    <footer className="main-footer">
                        <div className="container-fluid">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <span className="text-muted fs-7">
                                        © {new Date().getFullYear()} AdvertPro. All rights reserved.
                                    </span>
                                </div>
                                <div className="col-md-6 text-md-end">
                                    <a href="/help" className="text-muted me-3 fs-7">Help Center</a>
                                    <a href="/privacy" className="text-muted me-3 fs-7">Privacy Policy</a>
                                    <a href="/terms" className="text-muted fs-7">Terms of Service</a>
                                </div>
                            </div>
                        </div>
                    </footer>
                </div>
            </main>

            <style jsx>{`
                #outer-container {
                    height: 100vh;
                }

                .customer-layout {
                    display: flex;
                    min-height: 100vh;
                    background-color: #f8f9fa;
                    position: relative;
                }

                /* Desktop Sidebar */
                .desktop-sidebar {
                    width: 240px;
                    background: #ffffff;
                    color: #495057;
                    display: flex;
                    flex-direction: column;
                    position: fixed;
                    left: 0;
                    top: 0;
                    bottom: 0;
                    z-index: 1000;
                    border-right: 1px solid #dee2e6;
                    box-shadow: 2px 0 8px rgba(0,0,0,0.05);
                }

                .sidebar-content {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    width: 100%;
                }

                .sidebar-header {
                    background: #f8f9fa;
                }

                .brand-icon {
                    width: 32px;
                    height: 32px;
                }

                .user-avatar {
                    width: 40px;
                    height: 40px;
                }

                .user-avatar img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .avatar-placeholder {
                    width: 40px;
                    height: 40px;
                }

                .sidebar-nav-item {
                    color: #495057;
                    transition: all 0.15s ease;
                }

                .sidebar-nav-item:hover {
                    background-color: #f8f9fa;
                    color: #0d6efd;
                }

                .sidebar-nav-item.active {
                    background-color: #e7f1ff;
                    color: #0d6efd;
                    border-left: 3px solid #0d6efd;
                }

                .nav-icon {
                    font-size: 16px;
                }

                /* Main Content */
                .main-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    margin-left: 0;
                    min-height: 100vh;
                }

                @media (min-width: 768px) {
                    .main-content {
                        margin-left: 240px;
                    }
                }

                /* Fixed Top Header */
                .main-header {
                    background: white;
                    border-bottom: 1px solid #dee2e6;
                    padding: 0.75rem 1.25rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    z-index: 100;
                    height: 60px;
                    transition: all 0.2s ease;
                }

                @media (min-width: 768px) {
                    .main-header {
                        left: 240px;
                    }
                }

                .main-header.scrolled {
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }

                .header-left {
                    display: flex;
                    align-items: center;
                    flex: 1;
                }

                .sidebar-toggle {
                    padding: 0.25rem 0.5rem;
                }

                .header-search .input-group {
                    border-radius: 6px;
                }

                .header-search input {
                    font-size: 0.875rem;
                    padding: 0.375rem 0.75rem;
                }

                .header-search button {
                    padding: 0.375rem 0.75rem;
                }

                .header-right {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .header-actions {
                    display: flex;
                    gap: 0.5rem;
                }

                .notification-bell {
                    padding: 0.375rem 0.5rem;
                    border-radius: 6px;
                }

                .notification-badge {
                    position: absolute;
                    top: -4px;
                    right: -4px;
                    font-size: 0.6rem;
                    min-width: 16px;
                    height: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .user-dropdown-toggle {
                    padding: 0.375rem 0.5rem;
                    border-radius: 6px;
                }

                .user-avatar-sm {
                    width: 28px;
                    height: 28px;
                }

                .user-avatar-sm img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .avatar-placeholder-sm {
                    width: 28px;
                    height: 28px;
                    font-size: 0.75rem;
                }

                /* Content Container */
                .content-container {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    margin-top: 60px;
                }

                .content-wrapper {
                    flex: 1;
                    padding: 1.5rem;
                }

                .main-footer {
                    background: white;
                    border-top: 1px solid #dee2e6;
                    padding: 1rem 1.5rem;
                    margin-top: auto;
                }

                /* Responsive */
                @media (max-width: 767px) {
                    .header-search {
                        display: none;
                    }
                    
                    .header-actions {
                        display: none;
                    }
                    
                    .content-wrapper {
                        padding: 1rem;
                    }
                    
                    .user-info-sm {
                        display: none;
                    }
                }

                @media (max-width: 576px) {
                    .header-right {
                        gap: 0.5rem;
                    }
                    
                    .content-container {
                        margin-top: 56px;
                    }
                    
                    .main-header {
                        padding: 0.5rem 1rem;
                    }
                }

                /* Bootstrap font size overrides */
                :global(.dropdown-menu) {
                    font-size: 0.875rem;
                }

                :global(.badge) {
                    font-size: 0.75em;
                }

                :global(.btn-sm) {
                    font-size: 0.875rem;
                    padding: 0.25rem 0.5rem;
                }

                :global(.form-control-sm) {
                    font-size: 0.875rem;
                }

                :global(.fs-7) {
                    font-size: 0.8125rem !important;
                }
            `}</style>
        </div>
    );
};

export default CustomerLayout;